import {
  useState,
  useCallback,
  memo,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
} from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  type Announcements,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { Button, IconButton, Spinner } from "@narmi/design_system";
import { entities } from "byzantine";
import { useDebounce } from "byzantine/src/dbbl/hooks/useDebouncedEffect.hook";

import { useLocalization } from "@fluent/react";
import styles from "./QuickActionsEditor.module.scss";
import useQuickActionsEditorLabels from "./translations";
import { useQuickActionLabel } from "../useQuickActionLabel";

type ActionItem = API.QuickActionWithPosition;
type CatalogItem = API.QuickActionItem;

const QuickActionRow = memo(
  forwardRef<
    HTMLDivElement,
    {
      action: ActionItem;
      onRemove: (id: string) => void;
      isFixed?: boolean;
      labels: ReturnType<typeof useQuickActionsEditorLabels>;
    }
  >(({ action, onRemove, isFixed = false, labels }, ref) => {
    const quickAction = action.action;
    const translatedLabel = useQuickActionLabel(quickAction);
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: quickAction.key,
      disabled: isFixed,
    });

    const itemStyle = useMemo(
      () => ({
        transform: CSS.Transform.toString(transform),
        transition,
        boxShadow: isDragging ? "var(--shadow-m)" : undefined,
        zIndex: isDragging ? 999 : undefined,
      }),
      [transform, transition, isDragging]
    );

    const combineRefsCallback = useCallback(
      (node: HTMLDivElement) => {
        setNodeRef(node);
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [setNodeRef, ref]
    );

    if (isFixed) {
      return (
        <div ref={ref} className={`${styles.item} ${styles.fixedItem}`}>
          <span className={styles.lockIcon}>
            <span className="narmi-icon-lock" />
          </span>
          <span className={styles.iconWrapper}>
            <span className={`narmi-icon-${quickAction.icon_name}`} />
          </span>
          <span className={styles.label}>
            {translatedLabel} ({labels.requiredSuffix})
          </span>
        </div>
      );
    }

    return (
      <div
        ref={combineRefsCallback}
        style={itemStyle}
        className={`${styles.item} ${isDragging ? styles.dragging : ""}`}
      >
        <span
          {...attributes}
          {...listeners}
          className={styles.dragHandle}
          aria-label={labels.reorderAriaLabel}
          tabIndex={0}
        >
          <span className={`narmi-icon-menu ${styles.dragIcon}`} />
        </span>
        <span className={styles.iconWrapper}>
          <span className={`narmi-icon-${quickAction.icon_name}`} />
        </span>
        <span className={styles.label}>{translatedLabel}</span>
        <IconButton
          name="x"
          testId="remove-quick-action-button"
          onClick={useCallback(
            () => onRemove(quickAction.key),
            [onRemove, quickAction.key]
          )}
        />
      </div>
    );
  }),
  (prev, next) =>
    prev.action.action.key === next.action.action.key &&
    prev.isFixed === next.isFixed &&
    prev.labels === next.labels
);

const DragOverlayContent = memo(
  ({ action }: { action: ActionItem }) => {
    const translatedLabel = useQuickActionLabel(action.action);
    
    return (
      <div className={`${styles.item} ${styles.dragOverlay}`}>
        <span className={styles.dragHandle} aria-hidden="true">
          <span className={`narmi-icon-menu ${styles.dragIcon}`} />
        </span>
        <span className={styles.iconWrapper}>
          <span className={`narmi-icon-${action.action.icon_name}`} />
        </span>
        <span className={styles.label}>{translatedLabel}</span>
      </div>
    );
  }
);

const ActionLibraryItem = memo(
  forwardRef<
    HTMLButtonElement,
    {
      item: CatalogItem;
      onAdd: (item: CatalogItem) => void;
      isDisabled: boolean;
      disabledTooltip?: string;
      addButtonLabel: string;
    }
  >(({ item, onAdd, isDisabled, disabledTooltip, addButtonLabel }, ref) => {
    const translatedLabel = useQuickActionLabel(item);
    const handleAddCatalogItem = useCallback(() => {
      onAdd(item);
    }, [onAdd, item]);

    return (
      <div className={styles.item}>
        <span className={styles.iconWrapper}>
          <span className={`narmi-icon-${item.icon_name}`} />
        </span>
        <span className={styles.label}>{translatedLabel}</span>
        <Button
          ref={ref}
          className={styles.addButton}
          kind="primary"
          size="xs"
          label={addButtonLabel}
          testId="add-quick-action-button"
          onClick={handleAddCatalogItem}
          disabled={isDisabled}
          title={isDisabled ? disabledTooltip : undefined}
        />
      </div>
    );
  })
);

const QuickActionsEditor = () => {
  const labels = useQuickActionsEditorLabels();
  const { l10n } = useLocalization();
  const quickActions = entities.quickActions.useQuickActions();
  const catalogItems = entities.quickActions.useQuickActionsCatalog();
  const { send: fetchCatalog } =
    entities.quickActions.useFetchQuickActionsCatalog();
  const { send: postQuickActions, loading: isSaving } =
    entities.quickActions.usePostQuickActions();

  const [localActions, setLocalActions] = useState<ActionItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDebouncedSavePending, setIsDebouncedSavePending] = useState(false);

  // Refs for focus management
  const quickActionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const addButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const mountedRef = useRef<boolean>(true);

  // Prevents queued work from attempting to update after unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Helper to set refs
  const setQuickActionRef = useCallback(
    (key: string, element: HTMLDivElement | null) => {
      if (element) {
        quickActionRefs.current.set(key, element);
      } else {
        quickActionRefs.current.delete(key);
      }
    },
    []
  );

  const setAddButtonRef = useCallback(
    (catalogItemKey: string, element: HTMLButtonElement | null) => {
      if (element) {
        addButtonRefs.current.set(catalogItemKey, element);
      } else {
        addButtonRefs.current.delete(catalogItemKey);
      }
    },
    []
  );

  // Focus helpers
  const focusElement = useCallback(
    (element: HTMLElement | null | undefined) => {
      if (!mountedRef.current || !element) {
        return;
      }
      try {
        element.focus({ preventScroll: true });
      } catch {
        element.focus();
      }
    },
    []
  );

  const getFirstFocusableInItem = useCallback((container: HTMLElement) => {
    const dragHandle = container.querySelector(
      '[tabindex="0"]'
    ) as HTMLElement | null;
    if (dragHandle) {
      return dragHandle;
    }
    const button = container.querySelector("button") as HTMLElement | null;
    if (button) {
      return button;
    }
    return container;
  }, []);

  const focusItemById = useCallback(
    (key: string) => {
      const element = quickActionRefs.current.get(key);
      if (!element) {
        return;
      }
      focusElement(getFirstFocusableInItem(element));
    },
    [focusElement, getFirstFocusableInItem]
  );

  const focusNextAvailableAddButton = useCallback(
    (fromCatalogItemKey: string, updatedActions: ActionItem[]) => {
      const remainingCatalogItems = catalogItems.filter(
        (catalogItem) =>
          !updatedActions.some(
            (action) => action.action.key === catalogItem.key
          )
      );

      if (remainingCatalogItems.length === 0) {
        return;
      }

      const catalogItemOrder = catalogItems.map(
        (catalogItem) => catalogItem.key
      );
      const remainingItemsOrdered = remainingCatalogItems.sort(
        (firstItem, secondItem) =>
          catalogItemOrder.indexOf(firstItem.key) -
          catalogItemOrder.indexOf(secondItem.key)
      );

      const startIndex = remainingItemsOrdered.findIndex(
        (catalogItem) => catalogItem.key === fromCatalogItemKey
      );
      const nextIndex =
        startIndex >= 0 && startIndex < remainingItemsOrdered.length - 1
          ? startIndex + 1
          : 0;

      const nextCatalogItemKey = remainingItemsOrdered[nextIndex]?.key;
      if (!nextCatalogItemKey) {
        return;
      }

      const nextButton = addButtonRefs.current.get(nextCatalogItemKey);
      focusElement(nextButton || null);
    },
    [catalogItems, focusElement]
  );

  // Initialize local state when quickActions first loads
  useEffect(() => {
    if (quickActions.length > 0 && localActions.length === 0) {
      setLocalActions([...quickActions]);
    }
  }, [quickActions, localActions.length]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  // Debounced background save to keep the UI snappy during reorders/adds/removes
  const debouncedSave = useDebounce(
    useCallback(
      async (actionItems: ActionItem[]) => {
        await postQuickActions(actionItems.map(({ action }) => action.key));
        setIsDebouncedSavePending(false);
      },
      [postQuickActions]
    ),
    500
  );

  const triggerSave = useCallback(
    (actionItems: ActionItem[]) => {
      setIsDebouncedSavePending(true);
      debouncedSave(actionItems);
    },
    [debouncedSave]
  );

  const fixedAction = useMemo(
    () =>
      localActions.find((actionItem) => actionItem.action.key === "send_money"),
    [localActions]
  );

  const otherActions = useMemo(
    () =>
      localActions.filter(
        (actionItem) => actionItem.action.key !== "send_money"
      ),
    [localActions]
  );

  const availableCatalogItems = useMemo(
    () =>
      catalogItems
        .filter(
          (catalogItem) =>
            !localActions.some(
              (actionItem) => actionItem.action.key === catalogItem.key
            )
        )
        .sort((a, b) => a.label.localeCompare(b.label)),
    [catalogItems, localActions]
  );

  const updateActionsWithPositions = useCallback(
    (actionItems: ActionItem[]) =>
      actionItems.map((actionItem, index) => ({
        ...actionItem,
        position: index,
      })),
    []
  );

  const addQuickAction = useCallback(
    async (catalogItem: CatalogItem) => {
      setLocalActions((currentActions) => {
        if (currentActions.length >= 5) {
          return currentActions;
        }

        const newAction: ActionItem = {
          action: { ...catalogItem },
          position: currentActions.length,
        };

        const updatedActions = updateActionsWithPositions([
          ...currentActions,
          newAction,
        ]);

        triggerSave(updatedActions);

        // Focus management for adding items
        requestAnimationFrame(() => {
          if (!mountedRef.current) {
            return;
          }

          if (updatedActions.length >= 5) {
            focusItemById(newAction.action.key);
            return;
          }

          focusNextAvailableAddButton(catalogItem.key, updatedActions);
        });

        return updatedActions;
      });
    },
    [
      triggerSave,
      updateActionsWithPositions,
      focusItemById,
      focusNextAvailableAddButton,
    ]
  );

  const remove = useCallback(
    async (key: string) => {
      setLocalActions((currentActions) => {
        const removedIndex = currentActions.findIndex(
          (action) => action.action.key === key
        );
        const updatedActions = updateActionsWithPositions(
          currentActions.filter((actionItem) => actionItem.action.key !== key)
        );

        triggerSave(updatedActions);

        // Focus management for removing items
        requestAnimationFrame(() => {
          if (!mountedRef.current) {
            return;
          }

          if (updatedActions.length === 0) {
            const firstAvailable = catalogItems.find(
              (catalogItem) =>
                !updatedActions.some(
                  (action) => action.action.key === catalogItem.key
                )
            );
            if (firstAvailable) {
              const firstAvailableButton = addButtonRefs.current.get(
                firstAvailable.key
              );
              focusElement(firstAvailableButton || null);
            }
            return;
          }

          const nextFocusIndex = Math.min(
            removedIndex,
            updatedActions.length - 1
          );
          const nextAction = updatedActions[nextFocusIndex];
          if (!nextAction) {
            return;
          }

          focusItemById(nextAction.action.key);
        });

        return updatedActions;
      });
    },
    [
      triggerSave,
      updateActionsWithPositions,
      catalogItems,
      focusElement,
      focusItemById,
    ]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getActionLabelByKey = useCallback(
    (key: string) => {
      const match = localActions.find(
        (actionItem) => actionItem.action.key === key
      );
      if (!match) {
        return labels.itemFallback;
      }
      return l10n.getString(`quick-action-${match.action.key}`, {}, match.action.label);
    },
    [localActions, labels.itemFallback, l10n]
  );

  const announcements: Announcements = useMemo(
    () => ({
      onDragStart: ({ active }) =>
        labels.dragPickedUp(getActionLabelByKey(String(active.id))),
      onDragOver: ({ active, over }) => {
        if (!over) {
          return undefined;
        }
        return labels.dragMoving(
          getActionLabelByKey(String(active.id)),
          getActionLabelByKey(String(over.id))
        );
      },
      onDragEnd: ({ active, over }) => {
        if (!over) {
          return labels.dragDropped(getActionLabelByKey(String(active.id)));
        }
        return labels.dragDropped(
          getActionLabelByKey(String(active.id)),
          getActionLabelByKey(String(over.id))
        );
      },
      onDragCancel: ({ active }) =>
        labels.dragCancelled(getActionLabelByKey(String(active.id))),
    }),
    [labels, getActionLabelByKey]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    setActiveId(String(active.id));
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || active.id === over.id) {
        requestAnimationFrame(() => {
          if (!mountedRef.current) {
            return;
          }
          focusItemById(String(active.id));
        });
        return;
      }

      setLocalActions((currentActions) => {
        const fromIndex = currentActions.findIndex(
          (actionItem) => actionItem.action.key === String(active.id)
        );
        const toIndex = currentActions.findIndex(
          (actionItem) => actionItem.action.key === String(over.id)
        );

        if (fromIndex < 0 || toIndex < 0) {
          requestAnimationFrame(() => {
            if (!mountedRef.current) {
              return;
            }
            focusItemById(String(active.id));
          });
          return currentActions;
        }

        const reorderedActions = arrayMove(currentActions, fromIndex, toIndex);
        const actionsWithUpdatedPositions =
          updateActionsWithPositions(reorderedActions);

        triggerSave(actionsWithUpdatedPositions);

        requestAnimationFrame(() => {
          if (!mountedRef.current) {
            return;
          }
          focusItemById(String(active.id));
        });

        return actionsWithUpdatedPositions;
      });
    },
    [triggerSave, updateActionsWithPositions, focusItemById]
  );

  const sortableItemIds = useMemo(
    () => otherActions.map((actionItem) => actionItem.action.key),
    [otherActions]
  );

  const isAtMaxActions = localActions.length >= 5;

  const modifiers = useMemo(
    () => [restrictToVerticalAxis, restrictToWindowEdges],
    []
  );

  const activelyDraggedAction = useMemo(() => {
    if (!activeId) {
      return null;
    }
    return (
      otherActions.find((actionItem) => actionItem.action.key === activeId) ||
      null
    );
  }, [activeId, otherActions]);

  const overlayPortalTarget = useMemo(
    () => (typeof document !== "undefined" ? document.body : null),
    []
  );

  const dragOverlayNode = useMemo(
    () => (
      <DragOverlay
        dropAnimation={{
          duration: 150,
          easing: "cubic-bezier(0.2, 0, 0.2, 1)",
        }}
        style={{
          cursor: "grabbing",
        }}
      >
        {activelyDraggedAction ? (
          <DragOverlayContent action={activelyDraggedAction} />
        ) : null}
      </DragOverlay>
    ),
    [activelyDraggedAction]
  );

  return (
    <div className={styles.container}>
      {(isSaving || isDebouncedSavePending) && (
        <div className={styles.savingIndicator}>
          <Spinner size={20} />
        </div>
      )}
      <div className={styles.header}>
        <h2 className={styles.mainTitle}>{labels.editQuickActionsTitle}</h2>
        <p className={styles.description}>
          {labels.editQuickActionsDescription}
        </p>
      </div>
      <div className={styles.section}>
        <div className={styles.title}>
          {labels.yourQuickActionsTitle} ({localActions.length}/5)
        </div>

        <div className={styles.list}>
          {fixedAction && (
            <QuickActionRow
              ref={element => {
                setQuickActionRef(fixedAction.action.key, element);
              }
              }
              action={fixedAction}
              onRemove={remove}
              isFixed={true}
              labels={labels}
            />
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            modifiers={modifiers}
            accessibility={{ announcements }}
          >
            <SortableContext
              items={sortableItemIds}
              strategy={verticalListSortingStrategy}
            >
              {otherActions.map((actionItem) => (
                <QuickActionRow
                  key={actionItem.action.key}
                  ref={element => {
                    setQuickActionRef(actionItem.action.key, element);
                  }
                  }
                  action={actionItem}
                  onRemove={remove}
                  labels={labels}
                />
              ))}
            </SortableContext>

            {overlayPortalTarget
              ? createPortal(dragOverlayNode, overlayPortalTarget)
              : dragOverlayNode}
          </DndContext>
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.title}>{labels.actionLibraryTitle}</div>
        {availableCatalogItems.map((item) => (
          <ActionLibraryItem
            key={item.key}
            ref={element => {
              setAddButtonRef(item.key, element);
            }}
            item={item}
            onAdd={addQuickAction}
            isDisabled={isAtMaxActions}
            disabledTooltip={labels.limitReachedTooltip}
            addButtonLabel={labels.addButtonLabel}
          />
        ))}
      </div>
    </div>
  );
};

export default QuickActionsEditor;
